import React, { useState, useEffect } from "react";
import {
    Modal,
    ModalHeader,
    ModalBody,
    Form,
    Row,
    Col,
    Label,
    Input,
    Button,
    FormFeedback
} from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { updateUserPassword } from "../../slices/users/thunk"; // Using the thunk from users slice
import * as Yup from "yup";
import { useFormik } from "formik";

const PasswordUpdateModal = ({ isOpen, toggle, user }) => {
    const dispatch = useDispatch();
    const { actionLoading } = useSelector((state) => state.Operators);
    // We are likely updating a user in a context where state.Users updates are relevant, 
    // but the original file used Operators. We'll stick to what was there or adapt.
    // If we want state.Users.loading, we should swap. But for now let's minimally change.

    // Actually, earlier you asked to use updateUserPassword from users thunk. 
    // Let's ensure we use consistent loading state if possible or standard local state

    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const validationSchema = Yup.object({
        newPassword: Yup.string()
            .required("Please Enter New Password")
            .min(6, "Password must be at least 6 characters"),
        confirmPassword: Yup.string()
            .required("Please Enter Confirm Password")
            .oneOf([Yup.ref('newPassword'), null], 'Passwords must match')
    });

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            newPassword: "",
            confirmPassword: ""
        },
        validationSchema: validationSchema,
        onSubmit: async (values) => {
            if (user && (user._id || user.id)) {
                const userId = user._id || user.id;
                const updateData = {
                    newPassword: values.newPassword,
                    confirmPassword: values.confirmPassword
                };

                const result = await dispatch(updateUserPassword(userId, updateData));

                if (result.success || result.status === 1) {
                    toggle();
                    formik.resetForm();
                }
            }
        },
    });

    useEffect(() => {
        if (!isOpen) {
            formik.resetForm();
            setShowPassword(false);
            setShowConfirmPassword(false);
        }
    }, [isOpen]);

    const generatePassword = () => {
        let password = "";
        for (let i = 0; i < 6; i++) {
            password += Math.floor(Math.random() * 10);
        }
        formik.setFieldValue("newPassword", password);
        formik.setFieldValue("confirmPassword", password);
        setShowPassword(true);
        setShowConfirmPassword(true);
    };

    return (
        <Modal isOpen={isOpen} toggle={toggle}>
            <ModalHeader toggle={toggle}>
                Change Password for {user?.acnumber || "User"}
            </ModalHeader>
            <ModalBody>
                <Form onSubmit={(e) => {
                    e.preventDefault();
                    formik.handleSubmit();
                    return false;
                }}>
                    <Row>
                        <Col md={12}>
                            <div className="mb-3">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <Label className="form-label mb-0">New Password</Label>
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-info"
                                        onClick={generatePassword}
                                    >
                                        Generate Password
                                    </button>
                                </div>
                                <div className="position-relative auth-pass-inputgroup mb-3">
                                    <Input
                                        type={showPassword ? "text" : "password"}
                                        name="newPassword"
                                        placeholder="Enter new password"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.newPassword}
                                        invalid={formik.touched.newPassword && formik.errors.newPassword ? true : false}
                                        className="form-control pe-5"
                                    />
                                    <button
                                        className="btn btn-link position-absolute end-0 top-0 text-decoration-none text-muted"
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        style={{ zIndex: 10 }}
                                    >
                                        <i className={showPassword ? "ri-eye-off-fill" : "ri-eye-fill"}></i>
                                    </button>
                                    {formik.touched.newPassword && formik.errors.newPassword ? (
                                        <FormFeedback type="invalid">{formik.errors.newPassword}</FormFeedback>
                                    ) : null}
                                </div>
                            </div>
                        </Col>
                        <Col md={12}>
                            <div className="mb-3">
                                <Label className="form-label">Confirm Password</Label>
                                <div className="position-relative auth-pass-inputgroup mb-3">
                                    <Input
                                        type={showConfirmPassword ? "text" : "password"}
                                        name="confirmPassword"
                                        placeholder="Confirm new password"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.confirmPassword}
                                        invalid={formik.touched.confirmPassword && formik.errors.confirmPassword ? true : false}
                                        className="form-control pe-5"
                                    />
                                    <button
                                        className="btn btn-link position-absolute end-0 top-0 text-decoration-none text-muted"
                                        type="button"
                                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                        style={{ zIndex: 10 }}
                                    >
                                        <i className={showConfirmPassword ? "ri-eye-off-fill" : "ri-eye-fill"}></i>
                                    </button>
                                    {formik.touched.confirmPassword && formik.errors.confirmPassword ? (
                                        <FormFeedback type="invalid">{formik.errors.confirmPassword}</FormFeedback>
                                    ) : null}
                                </div>
                            </div>
                        </Col>
                    </Row>
                    <div className="hstack gap-2 justify-content-end">
                        <Button color="light" onClick={toggle}>Close</Button>
                        <Button color="primary" type="submit" disabled={actionLoading}>
                            {actionLoading ? "Updating..." : "Update Password"}
                        </Button>
                    </div>
                </Form>
            </ModalBody>
        </Modal>
    );
};

export default PasswordUpdateModal;
